import { Controller } from "@hotwired/stimulus"

// Zoom and pan for a floor plan (docs/06 "Zoom − + Fit", docs/07 "Map on
// touch devices").
//
// A venue drawn at true scale is 437 m across, and a 3 m stall on it is six
// pixels wide on a laptop. Nothing can be placed, or chosen, at that size, so
// every map — the editor, the picker, the public plan — gets the same
// viewport: wheel or pinch to zoom about the pointer, drag the background to
// pan, buttons and keys for the rest.
//
// It owns the <svg>'s viewBox and nothing else. Booths belong to whichever
// controller draws them; this one never reads the viewBox from memory, only
// from the element, so a controller that re-frames the map on its own (the
// picker, when the zone changes) is never argued with.
//
//   data-home-view-box  the window Fit returns to (the zone, for the picker;
//                       absent, the one the page loaded with)
//   data-full-view-box  the whole venue, and the furthest the map zooms out
//                       (absent, the one the page loaded with)
//   wheel value         "focused" (the default): the wheel zooms only once the
//                       map has been clicked or tabbed to, or with ctrl/meta
//                       held, so a page that scrolls past a 70vh map is not
//                       caught in it; "always": a tool, like the editor
//   stage target        the element Full screen takes over (the card holding
//                       the toolbar and the map); absent, no full screen
//   fullscreenToggle    the button that says which state we are in
export default class extends Controller {
  static targets = ["svg", "readout", "stage", "fullscreenToggle"]
  static values = {
    // Mirrors FloorPlan::UNITS_PER_METRE; every view passes it.
    unitsPerMetre: { type: Number, default: 10 },
    wheel: { type: String, default: "focused" }
  }

  // The narrowest window: a fortieth of the venue, about 11 m on the 2026
  // plan and still a close-up on a small test map.
  static MIN_FRACTION = 1 / 40
  // One press of + or − changes the window by this much.
  static ZOOM_STEP = 1.4
  // Wheel sensitivity: a trackpad pinch arrives as a wheel with ctrlKey at a
  // coarser step than a scroll wheel.
  static WHEEL_PINCH = 0.01
  static WHEEL_SCROLL = 0.0015
  // A press has to travel this far before it is a pan rather than a tap.
  static SLOP_PX = 4
  // Below this many feet across, the readout keeps a decimal.
  static WHOLE_FEET_FROM = 65
  // FloorPlan::FEET_PER_METRE; floor_plan_controller.js carries the same.
  static FEET_PER_METRE = 3.28084
  // How long after the last wheel tick a gesture counts as over.
  static GESTURE_SETTLE_MS = 300

  connect() {
    this.initial = this.svgTarget.getAttribute("viewBox")
    this.pointers = new Map()
    this.pinchDistance = null
    this.panning = false
    this.pending = null
    this.frameHandle = null
    this.forcedAspect = null
    this.refitWhenVisible = false
    // A map framed while hidden (the picker's list view is the default when
    // its booths are small) could not be measured; the moment it has a size
    // the frame is taken again, so it does not come up letterboxed.
    this.resizeObserver = new ResizeObserver(() => {
      if (this.refitWhenVisible && this.svgTarget.getBoundingClientRect().width > 0) {
        this.refitWhenVisible = false
        this.fit()
      }
      this.report()
    })
    this.resizeObserver.observe(this.svgTarget)
    this.onFullscreenChange = () => this.fullscreenChanged()
    document.addEventListener("fullscreenchange", this.onFullscreenChange)
    this.renderFullscreenToggle()
    this.report()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    document.removeEventListener("fullscreenchange", this.onFullscreenChange)
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle)
    clearTimeout(this.settle)
  }

  // --- Full screen ------------------------------------------------------------

  // The stage (toolbar and map together) takes the whole screen, so a stall
  // can be placed with the venue around it rather than through a letterbox
  // beside a sidebar. The browser's own full screen, so Esc always gets out.
  // The map's height follows its window's shape, so on the way in the window
  // is grown to the screen's shape and on the way out back to the venue's.
  toggleFullscreen() {
    if (!this.hasStageTarget || !document.fullscreenEnabled) return
    if (document.fullscreenElement === this.stageTarget) document.exitFullscreen()
    else this.stageTarget.requestFullscreen({ navigationUI: "hide" }).catch(() => this.renderFullscreenToggle())
  }

  get fullscreen() {
    return this.hasStageTarget && document.fullscreenElement === this.stageTarget
  }

  fullscreenChanged() {
    if (this.fullscreen) {
      // Measured after the browser has laid the stage out at full size: the
      // room left under the toolbar is what the map may have.
      requestAnimationFrame(() => {
        const stage = this.stageTarget.getBoundingClientRect()
        const svg = this.svgTarget.getBoundingClientRect()
        const bottom = parseFloat(getComputedStyle(this.stageTarget).paddingBottom) || 0
        const height = stage.bottom - svg.top - bottom
        this.forcedAspect = height > 0 && svg.width > 0 ? svg.width / height : null
        this.apply(this.fitted(this.current()))
        this.report()
        this.svgTarget.focus({ preventScroll: true })
      })
    } else {
      this.forcedAspect = null
      this.apply(this.fitted(this.current()))
      this.report()
    }
    this.renderFullscreenToggle()
  }

  renderFullscreenToggle() {
    if (!this.hasFullscreenToggleTarget) return
    const on = this.fullscreen
    this.fullscreenToggleTarget.setAttribute("aria-pressed", String(on))
    this.fullscreenToggleTarget.textContent = on ? "Exit full screen" : "Full screen"
    this.fullscreenToggleTarget.hidden = !document.fullscreenEnabled
  }

  // --- Buttons and keys ---------------------------------------------------

  zoomIn() { this.zoomAbout(1 / this.constructor.ZOOM_STEP, undefined, undefined, true); this.report() }
  zoomOut() { this.zoomAbout(this.constructor.ZOOM_STEP, undefined, undefined, true); this.report() }

  fit() { this.apply(this.fitted(this.parse(this.svgTarget.dataset.homeViewBox || this.initial))); this.report() }
  showAll() { this.apply(this.fitted(this.parse(this.svgTarget.dataset.fullViewBox || this.initial))); this.report() }

  // A window handed over by the page: the zone a chip names, for instance.
  frame(event) {
    const box = event.params?.viewBox
    if (!box) return
    this.apply(this.fitted(this.parse(box)))
    this.report()
  }

  // A window grown to the shape of the box it is shown in, about its centre,
  // so a tall zone — a road of stalls — fills the view rather than standing
  // as a strip in the middle of it. The server's window is what to show;
  // what shape the screen is, only the browser knows.
  fitted(box) {
    const aspect = this.aspect()
    if (!aspect) {
      this.refitWhenVisible = true
      return box
    }
    let { width, height } = box
    if (width / height < aspect) width = height * aspect
    else height = width / aspect
    // A window grown past the venue would be clamped back to the venue's
    // width and lose height with it, cutting the map off. Left as it came,
    // the browser letterboxes it instead (preserveAspectRatio meet). Not so
    // for a map whose own height follows the window: there the clamp keeps
    // the shape, and the shape is the point.
    const full = this.full()
    if (!this.followsWindow() && (width > full.width + 0.01 || height > full.height + 0.01)) return box
    return { x: box.x + (box.width - width) / 2, y: box.y + (box.height - height) / 2, width, height }
  }

  // The shape a framed window should take. Full screen dictates it; a map in
  // a fixed box takes the box's; a map whose height follows its window (the
  // editor's, block-size auto) would only hand back whatever it has now, so
  // it takes the venue's own shape instead.
  aspect() {
    if (this.forcedAspect) return this.forcedAspect
    const rect = this.svgTarget.getBoundingClientRect()
    if (!rect.width || !rect.height) return null
    if (this.followsWindow()) {
      const full = this.full()
      return full.width / full.height
    }
    return rect.width / rect.height
  }

  // True when the svg's box has the same shape as its window: its height is
  // following the viewBox rather than a container.
  followsWindow() {
    const rect = this.svgTarget.getBoundingClientRect()
    const box = this.current()
    if (!rect.width || !rect.height || !box.width || !box.height) return false
    return Math.abs((rect.width / rect.height) - (box.width / box.height)) < 0.02
  }

  key(event) {
    if (event.target !== this.svgTarget) return
    if (event.key === "+" || event.key === "=") this.zoomIn()
    else if (event.key === "-" || event.key === "_") this.zoomOut()
    else if (event.key === "0") this.fit()
    else if (event.key === "f" || event.key === "F") this.toggleFullscreen()
    else return
    event.preventDefault()
  }

  // --- Wheel ----------------------------------------------------------------

  // Registered with :!passive in the markup, or preventDefault is ignored and
  // the page scrolls under the map.
  wheel(event) {
    const pinch = event.ctrlKey || event.metaKey
    if (this.wheelValue !== "always" && !pinch && document.activeElement !== this.svgTarget) return

    event.preventDefault()
    const { WHEEL_PINCH, WHEEL_SCROLL, GESTURE_SETTLE_MS } = this.constructor
    this.zoomAbout(Math.exp(event.deltaY * (pinch ? WHEEL_PINCH : WHEEL_SCROLL)), event.clientX, event.clientY, false)
    clearTimeout(this.settle)
    this.settle = setTimeout(() => this.report(), GESTURE_SETTLE_MS)
  }

  // --- Pointers: drag to pan, two fingers to pinch ------------------------------

  pointerDown(event) {
    if (event.button !== 0 && event.pointerType === "mouse") return
    // A third touch, or a pointer whose release was never seen, is not a
    // pinch partner: start the gesture afresh.
    if (this.pointers.size >= 2) this.pointers.clear()
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY })
  }

  pointerMove(event) {
    const pointer = this.pointers.get(event.pointerId)
    if (!pointer) return

    const previous = { x: pointer.x, y: pointer.y }
    pointer.x = event.clientX
    pointer.y = event.clientY

    if (this.pointers.size === 1) {
      // Capture only once this is a pan. Capturing on the press would
      // retarget the click that follows a tap, and a tap on a booth is how
      // the picker chooses one.
      if (!this.panning) {
        const travelled = Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY)
        if (travelled < this.constructor.SLOP_PX) return
        this.startPanning(event.pointerId)
      }

      const scale = this.pixelsPerUnit()
      const box = this.current()
      this.apply({ ...box, x: box.x - (event.clientX - previous.x) / scale, y: box.y - (event.clientY - previous.y) / scale }, false)
    } else if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()]
      const distance = Math.hypot(a.x - b.x, a.y - b.y)
      if (this.pinchDistance) this.zoomAbout(this.pinchDistance / distance, (a.x + b.x) / 2, (a.y + b.y) / 2, false)
      this.pinchDistance = distance
      this.startPanning()
    }
  }

  // Also wired to pointerleave and lostpointercapture, so a press that leaves
  // the map before it became a pan is forgotten rather than left to turn the
  // next touch into a phantom pinch.
  pointerUp(event) {
    this.pointers.delete(event.pointerId)
    this.pinchDistance = null
    if (this.pointers.size === 0) {
      this.svgTarget.removeAttribute("data-panning")
      this.report()
      // Left true until the click that follows has been and gone, so a pan
      // that ends over a booth does not choose it.
      setTimeout(() => { this.panning = false }, 0)
    }
  }

  // A click at the end of a pan is not a click on what happens to be under
  // the pointer. Capture phase, so it is stopped before any booth hears it.
  click(event) {
    if (!this.panning) return
    event.stopPropagation()
    event.preventDefault()
  }

  startPanning(pointerId) {
    this.panning = true
    this.svgTarget.setAttribute("data-panning", "")
    if (pointerId !== undefined) this.svgTarget.setPointerCapture?.(pointerId)
  }

  // --- Geometry -------------------------------------------------------------

  // Zoom by a factor about a point on the screen, or about the centre. The
  // limits are on what is VISIBLE, not on the window's width: a tall window
  // in a wide box shows far more than its width says, and clamping the width
  // would stop the zoom while the map was still far out.
  zoomAbout(factor, clientX, clientY, immediate = true) {
    const box = this.current()
    const full = this.full()
    const visible = this.visibleWidth(box)
    const narrowest = full.width * this.constructor.MIN_FRACTION
    const widest = this.visibleWidth(full)
    const target = Math.min(widest, Math.max(narrowest, visible * factor))
    factor = target / visible
    if (Math.abs(factor - 1) < 1e-6) return

    const anchor = clientX === undefined ? { x: box.x + box.width / 2, y: box.y + box.height / 2 } : this.toMap(clientX, clientY)

    this.apply({
      x: anchor.x - (anchor.x - box.x) * factor,
      y: anchor.y - (anchor.y - box.y) * factor,
      width: box.width * factor,
      height: box.height * factor
    }, immediate)
  }

  // Writes a window, kept inside the venue and never wider than it. The
  // aspect ratio is whatever the window came with. A button or key writes at
  // once; a pointer or wheel stream (immediate false) is coalesced to one
  // write per frame, because it fires faster than the screen can redraw and
  // every write re-rasterises the drawing underneath.
  apply(box, immediate = true) {
    const full = this.full()
    const width = Math.min(box.width, full.width)
    const height = box.height * (width / box.width)
    const x = this.clamp(box.x, full.x, full.x + full.width - width)
    const y = this.clamp(box.y, full.y, full.y + full.height - height)
    const value = [x, y, width, height].map((n) => Math.round(n * 100) / 100).join(" ")

    if (immediate) {
      if (this.frameHandle) cancelAnimationFrame(this.frameHandle)
      this.frameHandle = null
      this.pending = null
      this.svgTarget.setAttribute("viewBox", value)
      return
    }

    this.pending = value
    if (this.frameHandle) return
    this.frameHandle = requestAnimationFrame(() => {
      this.frameHandle = null
      if (this.pending) this.svgTarget.setAttribute("viewBox", this.pending)
      this.pending = null
    })
  }

  // The window as it stands, including a write still waiting for its frame.
  current() {
    return this.parse(this.pending || this.svgTarget.getAttribute("viewBox"))
  }

  full() {
    return this.parse(this.svgTarget.dataset.fullViewBox || this.initial)
  }

  parse(string) {
    const [x, y, width, height] = string.trim().split(/[\s,]+/).map(Number)
    return { x, y, width, height }
  }

  // Through the SVG's own matrix, so a letterboxed map (one whose box is a
  // different shape from its window) still converts correctly.
  toMap(clientX, clientY) {
    const matrix = this.svgTarget.getScreenCTM()
    if (!matrix) return this.current()
    return new DOMPoint(clientX, clientY).matrixTransform(matrix.inverse())
  }

  // The shape of the box the map is drawn in, or null while it has none.
  boxAspect() {
    const rect = this.svgTarget.getBoundingClientRect()
    return rect.width && rect.height ? rect.width / rect.height : null
  }

  // How many units are actually across the box for a window: the window's
  // own width, or more when the window is taller than the box and the
  // browser letterboxes it (preserveAspectRatio meet).
  visibleWidth(box) {
    const aspect = this.boxAspect()
    return aspect ? Math.max(box.width, box.height * aspect) : box.width
  }

  pixelsPerUnit() {
    const rect = this.svgTarget.getBoundingClientRect()
    const box = this.current()
    return Math.min(rect.width / box.width, rect.height / box.height) || 1
  }

  clamp(value, low, high) {
    return high < low ? (low + high) / 2 : Math.min(Math.max(value, low), high)
  }

  // "44 m across": what the window shows, in the venue's own measure. The
  // readout is a live region, so it is written at the end of a gesture and
  // on each button or key — not on every frame of a drag, which would queue
  // a screen reader a stream of numbers.
  report() {
    if (!this.hasReadoutTarget) return
    // In feet only, like every size on the site (2026-10-18).
    const feet = this.visibleWidth(this.current()) / this.unitsPerMetreValue * this.constructor.FEET_PER_METRE
    const whole = feet >= this.constructor.WHOLE_FEET_FROM
    const text = `${(whole ? Math.round(feet) : +feet.toFixed(1)).toLocaleString("en")} ft across`
    if (this.readoutTarget.textContent !== text) this.readoutTarget.textContent = text
  }
}
